import { useCallback } from "react";
import {
  getMyLayouts,
  createLayout,
  duplicateLayout,
  renameLayout,
  deleteLayout,
  activateLayout,
  setLayoutShareToken,
  importLayoutByShareToken,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";

export function useLayoutsLibrary(scope: "dashboard" | "pfm" | "custom-page" | "journal" | "analytics" | "sessions" | "discipline" = "dashboard") {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: layouts, refetch: refetchLayouts } = useQuery(getMyLayouts, { scope });

  const createFn = useAction(createLayout);
  const duplicateFn = useAction(duplicateLayout);
  const renameFn = useAction(renameLayout);
  const deleteFn = useAction(deleteLayout);
  const activateFn = useAction(activateLayout);
  const setShareTokenFn = useAction(setLayoutShareToken);
  const importFn = useAction(importLayoutByShareToken);

  const handleError = useCallback(
    (err: any, fallback: string) => {
      const msg =
        err?.data?.message ||
        err?.response?.data?.message ||
        err?.message ||
        fallback;
      toast({ description: msg, variant: "destructive" });
    },
    [toast],
  );

  const create = useCallback(
    async (name: string) => {
      try {
        const created = await (createFn as any)({ name, scope });
        await refetchLayouts();
        return created;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return null;
      }
    },
    [createFn, refetchLayouts, handleError, t, scope],
  );

  const duplicate = useCallback(
    async (layoutId: string, name?: string) => {
      try {
        const created = await (duplicateFn as any)({ layoutId, name });
        await refetchLayouts();
        return created;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return null;
      }
    },
    [duplicateFn, refetchLayouts, handleError, t],
  );

  const rename = useCallback(
    async (layoutId: string, name: string) => {
      const target = (layouts as any[])?.find((l: any) => l.id === layoutId);
      if (target?.isReadOnly) return;
      try {
        await (renameFn as any)({ layoutId, name });
        await refetchLayouts();
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
      }
    },
    [renameFn, refetchLayouts, handleError, t, layouts],
  );

  const remove = useCallback(
    async (layoutId: string) => {
      const target = (layouts as any[])?.find((l: any) => l.id === layoutId);
      if (target?.isReadOnly) return;
      try {
        await (deleteFn as any)({ layoutId });
        await refetchLayouts();
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
      }
    },
    [deleteFn, refetchLayouts, handleError, t, layouts],
  );

  const activate = useCallback(
    async (layoutId: string): Promise<boolean> => {
      try {
        await (activateFn as any)({ layoutId });
        await refetchLayouts();
        return true;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return false;
      }
    },
    [activateFn, refetchLayouts, handleError, t],
  );

  const setShareEnabled = useCallback(
    async (layoutId: string, enabled: boolean): Promise<string | null> => {
      try {
        const result = await (setShareTokenFn as any)({ layoutId, enabled });
        await refetchLayouts();
        return (result as any)?.shareToken ?? null;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return null;
      }
    },
    [setShareTokenFn, refetchLayouts, handleError, t],
  );

  const setShareSettings = useCallback(
    async (layoutId: string, opts: {
      enabled: boolean;
      shareInLibrary?: boolean;
      shareDescription?: string;
      shareDisplayName?: string;
    }): Promise<string | null> => {
      try {
        const result = await (setShareTokenFn as any)({ layoutId, ...opts });
        await refetchLayouts();
        return (result as any)?.shareToken ?? null;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return null;
      }
    },
    [setShareTokenFn, refetchLayouts, handleError, t],
  );

  const importByToken = useCallback(
    async (shareToken: string) => {
      try {
        const result = await (importFn as any)({ shareToken });
        await refetchLayouts();
        toast({ description: t.dashboard.layouts.importedSuccess, duration: 2000 });
        return result;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return null;
      }
    },
    [importFn, refetchLayouts, handleError, toast, t],
  );

  const importWithDestination = useCallback(
    async (
      shareTokenOrId: string,
      destination: "current" | "new-page" | "save-only",
      opts?: { currentLayoutId?: string; newPageName?: string; useLayoutId?: boolean }
    ) => {
      const { useLayoutId, ...restOpts } = opts ?? {};
      const payload = useLayoutId
        ? { layoutId: shareTokenOrId, destination, ...restOpts }
        : { shareToken: shareTokenOrId, destination, ...restOpts };
      try {
        const result = await (importFn as any)(payload);
        await refetchLayouts();
        toast({ description: t.dashboard.layouts.importedSuccess, duration: 2000 });
        return result;
      } catch (e) {
        handleError(e, t.dashboard.widgetSaveError);
        return null;
      }
    },
    [importFn, refetchLayouts, handleError, toast, t],
  );

  const activeLayout =
    Array.isArray(layouts) ? (layouts as any[]).find((l) => l.isActive) ?? null : null;

  return {
    layouts: (layouts ?? []) as any[],
    activeLayout,
    create,
    duplicate,
    rename,
    remove,
    activate,
    setShareEnabled,
    setShareSettings,
    importByToken,
    importWithDestination,
    refetchLayouts,
  };
}
